import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  List,
  ListItem,
  IconButton,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Alert,
  CircularProgress,
  Switch,
  FormControlLabel,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getCredentialPanelStyles } from "../../../theme/panelStyles";
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
} from "@mui/icons-material";
import { JTLCredentialsApi } from "../../../services/api/jtl/jtl_credentials";

const JTLCredentials = () => {
  const theme = useTheme();
  const cp = getCredentialPanelStyles(theme);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const availableJTLScopes = [
    {
      value: "ffn.fulfiller.read",
      label: "Fullfiller Read Orders",
      description: "Allows reading of order data from JTL.",
      usage: "Fullfiller",
      defaultEnabled: false,
    },
    {
      value: "ffn.fulfiller.write",
      label: "Fullfiller Write Orders",
      description: "Allows writing of order data to JTL.",
      usage: "Fullfiller",
      defaultEnabled: false,
    },
    {
      value: "ffn.merchant.read",
      label: "Merchant Read Orders",
      description: "Allows reading of order data from JTL.",
      usage: "Merchant",
      defaultEnabled: true,
    },
    {
      value: "ffn.merchant.write",
      label: "Merchant Write Orders",
      description: "Allows writing of order data to JTL.",
      usage: "Merchant",
      defaultEnabled: false,
    },
  ];

  // Form states
  const [credentialForm, setCredentialForm] = useState({
    scopes: [],
  });
  const [JTLForm, setJTLForm] = useState({
    name: "",
    client_id: "",
    client_secret: "",
    callback_url: "http://localhost:8003/api/jtl/jtl/oauth/callback",
    scopes: [],
  });

  const [JTLCredentials, setJTLCredentials] = useState([]);
  const [JTLcredentialDialog, setJTLCredentialDialog] = useState(false);
  const [JTLAuthUrlDialog, setJTLAuthUrlDialog] = useState(false);
  const [JTLAuthUrl, setJTLAuthUrl] = useState("");
  const [editingJTLCredential, setEditingJTLCredential] = useState(null);

  // WebSocket state (not currently used, but referenced in useEffect)
  const [websocket, setWebsocket] = useState(null);

  // Google auth dialog states (referenced at bottom but not used for JTL)
  const [authUrlDialog, setAuthUrlDialog] = useState(false);
  const [authUrl, setAuthUrl] = useState("");

  useEffect(() => {
    // Load initial data
    loadJTLCredentials();

    const defaultJTLScopes = availableJTLScopes
      .filter((scope) => scope.defaultEnabled)
      .map((scope) => scope.value);

    setJTLForm((prev) => ({
      ...prev,
      scopes: defaultJTLScopes,
    }));
  }, []);

  // WebSocket message handler for JTL callback auth
  useEffect(() => {
    if (!websocket) return;
    const handleMessage = (data) => {
      if (data && data.type === "callback_auth" && data.success) {
        setJTLAuthUrlDialog(false);
        loadJTLCredentials();
      }
    };
    websocket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleMessage(data);
      } catch (error) {
        // ignore parse errors
      }
    };
    return () => {
      websocket.onmessage = null;
    };
  }, [websocket]);

  // Cleanup WebSocket on component unmount
  useEffect(() => {
    return () => {
      if (websocket) {
        console.log("Cleaning up WebSocket on component unmount");
        websocket.close();
        setWebsocket(null);
      }
    };
  }, [websocket]);

  const loadJTLCredentials = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      const CACHE_KEY = "jtl_credentials_cache";
      const CACHE_DURATION = 300 * 1000; // 300 seconds

      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
              setJTLCredentials(data);
              setLoading(false);
              return;
            }
          } catch (e) {
            console.error("Error parsing cache", e);
            localStorage.removeItem(CACHE_KEY);
          }
        }
      }

      const response = await JTLCredentialsApi.getCredentials();
      const credentials = response.data || [];
      setJTLCredentials(credentials);

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: credentials,
          timestamp: Date.now(),
        }),
      );
    } catch (error) {
      console.error("Failed to load JTL credentials:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to load JTL credentials",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleJTLCredentialSubmit = async () => {
    if (
      !JTLForm.name ||
      !JTLForm.client_id ||
      !JTLForm.client_secret ||
      !JTLForm.callback_url
    ) {
      setError("Please fill in all required JTL fields");
      return;
    }
    try {
      setLoading(true);
      setError(null);

      if (editingJTLCredential) {
        // Update existing credential
        await JTLCredentialsApi.updateCredential(
          editingJTLCredential.id,
          JTLForm,
        );
        setSuccess("JTL credential updated successfully!");
        await loadJTLCredentials(true);
        setJTLCredentialDialog(false);
        setEditingJTLCredential(null);
      } else {
        // Create new credential
        const response = await JTLCredentialsApi.createCredential(JTLForm);

        const auth_url_response = await JTLCredentialsApi.getAuthUrl(
          response.data.credential_id,
        );

        if (auth_url_response.data?.auth_url) {
          setJTLAuthUrl(auth_url_response.data.auth_url);
          setJTLAuthUrlDialog(true);
          setSuccess("JTL OAuth flow initiated successfully!");
        } else {
          setSuccess(
            "JTL OAuth flow initiated! Check the response for next steps.",
          );
        }

        setJTLCredentialDialog(false);
      }
    } catch (error) {
      console.error("Failed to save JTL credential:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to save JTL credential",
      );
    } finally {
      setLoading(false);
    }
  };

  const editJTLCredential = (credential) => {
    setEditingJTLCredential(credential);
    setJTLForm({
      name: credential.name || "",
      client_id: credential.client_id || "",
      client_secret: credential.client_secret || "",
      callback_url: credential.callback_url || "",
      scopes: Array.isArray(credential.scopes) ? credential.scopes : [],
    });
    setJTLCredentialDialog(true);
  };

  const deleteJTLCredential = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);
      await JTLCredentialsApi.deleteCredential(credentialId);
      await loadJTLCredentials(true);
      setSuccess("JTL Credential deleted successfully!");
    } catch (error) {
      console.error("Failed to delete JTL credential:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to delete JTL credential",
      );
    } finally {
      setLoading(false);
    }
  };

  const toggleActiveJTLCredential = async (credentialId) => {
    try {
      setLoading(true);
      setError(null);
      const response =
        await JTLCredentialsApi.toggleActiveCredential(credentialId);
      setSuccess(
        `JTL Credential ${
          response.data.active ? "activated" : "deactivated"
        } successfully!`,
      );
      await loadJTLCredentials(true);
    } catch (error) {
      console.error("Failed to toggle JTL credential status:", error);
      setError(
        error.response?.data?.detail ||
          error.message ||
          "Failed to toggle JTL credential status",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleJTLAuthDialogClose = async () => {
    setJTLAuthUrlDialog(false);

    setTimeout(async () => {
      await loadJTLCredentials(true);
    }, 2000);
  };

  const handleAuthDialogClose = async () => {
    setAuthUrlDialog(false);

    setTimeout(async () => {
      await loadJTLCredentials(true);
    }, 2000);
  };

  const getTokenStatus = (credential) => {
    if (!credential.expires_at) {
      return { status: "unknown", color: "warning", label: "Unknown" };
    }

    const expiresAt = new Date(credential.expires_at);
    const now = new Date();
    const timeDiff = expiresAt.getTime() - now.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

    if (timeDiff <= 0) {
      return { status: "expired", color: "error", label: "Expired" };
    } else if (daysDiff <= 7) {
      return {
        status: "expiring",
        color: "warning",
        label: `Expires in ${daysDiff} day(s)`,
      };
    } else {
      return {
        status: "valid",
        color: "success",
        label: `Valid (${daysDiff} days left)`,
      };
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card sx={cp.outerCard}>
          <CardContent sx={{ p: 4 }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
              sx={{
                ...cp.header,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  marginRight: "auto",
                  paddingRight: 2,
                }}
              >
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                  📦 JTL API Credentials
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Manage your JTL API access credentials
                </Typography>
              </Box>
              <Box sx={{ display: "flex", gap: 2, marginLeft: "auto" }}>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={loadJTLCredentials}
                  disabled={loading}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Reload
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setJTLCredentialDialog(true)}
                  sx={{
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.3)",
                      transform: "translateY(-2px)",
                    },
                    transition: "all 0.3s ease",
                    whiteSpace: "nowrap",
                  }}
                >
                  Add Credentials
                </Button>
              </Box>
            </Box>

            <Alert
              severity="info"
              sx={cp.infoAlert}
              icon={<CheckCircleIcon sx={{ color: "info.main" }} />}
            >
              <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
                📋 How to Get Your JTL Credentials:
              </Typography>
              <ol style={{ margin: 0, paddingLeft: "20px" }}>
                <li>
                  Go to{" "}
                  <a
                    href="https://kundencenter.jtl-software.de/oauth"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#1976d2",
                      textDecoration: "underline",
                      fontWeight: "bold",
                    }}
                  >
                    JTL Customer Center OAuth
                  </a>
                </li>
                <li>Create a OAuth Client or select an existing one</li>
                <li>Choose a Displayname scopes and a redirect URL</li>
                <li>
                  The Redirect URL must match the one you set in your
                  application - Ask Joel for help
                </li>
                <li>
                  Scopes must include: <code>ffn.merchant.read</code> - need to
                  match the one you set in your application aswell
                </li>
                <li>
                  Save and make sure that all the values form JTL and in teh
                  application match
                </li>
              </ol>
            </Alert>

            {JTLCredentials.length === 0 ? (
              <Box sx={cp.emptyState}>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, color: "text.secondary" }}
                >
                  No Credentials Found
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ mb: 3, color: "text.secondary" }}
                >
                  Add your first JTL credential to get started
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setJTLCredentialDialog(true)}
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    ...cp.ctaButton,
                  }}
                >
                  Add Your First Credential
                </Button>
              </Box>
            ) : (
              <List
                sx={{
                  width: "100%",
                  border: "2px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  boxShadow: 2,
                }}
              >
                {loading && JTLCredentials.length === 0 ? (
                  <ListItem>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        width: "100%",
                        py: 4,
                      }}
                    >
                      <CircularProgress />
                    </Box>
                  </ListItem>
                ) : (
                  <>
                    {JTLCredentials.map((credential, index) => (
                      <ListItem
                        key={credential.id}
                        divider={index < JTLCredentials.length - 1}
                        sx={{
                          px: 4,
                          py: 3,
                          borderBottom:
                            index < JTLCredentials.length - 1
                              ? "2px solid"
                              : "none",
                          borderColor: "divider",
                          "&:hover": {
                            backgroundColor: "action.hover",
                            transform: "translateX(4px)",
                          },
                          transition: "all 0.2s ease",
                          borderRadius:
                            index === 0
                              ? "8px 8px 0 0"
                              : index === JTLCredentials.length - 1
                                ? "0 0 8px 8px"
                                : 0,
                        }}
                      >
                        <Grid container alignItems="center" spacing={3}>
                          <Grid item xs={12} md={8}>
                            <Typography variant="h6">
                              {credential.name || "JTL Credential"}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                wordBreak: "break-all",
                                pr: 2,
                              }}
                            >
                              Client ID: {credential.client_id}
                            </Typography>
                            <Box sx={{ mt: 1 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Scopes:{" "}
                                {Array.isArray(credential.scopes)
                                  ? credential.scopes.join(", ")
                                  : credential.scopes}
                              </Typography>
                            </Box>
                            <Box
                              sx={{
                                mt: 0.5,
                                display: "flex",
                                gap: 1,
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              <Chip
                                label={
                                  credential.authorized
                                    ? "Authorized"
                                    : "Unknown"
                                }
                                size="small"
                                color={
                                  credential.authorized ? "success" : "error"
                                }
                              />
                              {credential.expires_at && (
                                <Chip
                                  label={getTokenStatus(credential).label}
                                  size="small"
                                  color={getTokenStatus(credential).color}
                                  variant="outlined"
                                />
                              )}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Created:{" "}
                                {credential.created_at
                                  ? new Date(
                                      credential.created_at,
                                    ).toLocaleDateString()
                                  : "Unknown"}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid
                            item
                            xs={12}
                            md={4}
                            sx={{
                              display: "flex",
                              justifyContent: { xs: "center", md: "flex-end" },
                              alignItems: "center",
                              gap: 1,
                              flexWrap: "wrap",
                            }}
                          >
                            {/* Active Toggle Switch */}
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                mr: 2,
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <Switch
                                    checked={credential.active || false}
                                    onChange={() =>
                                      toggleActiveJTLCredential(credential.id)
                                    }
                                    disabled={loading}
                                    size="small"
                                    sx={{
                                      "& .MuiSwitch-switchBase.Mui-checked": {
                                        color: "success.main",
                                      },
                                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                        {
                                          backgroundColor: "success.main",
                                        },
                                    }}
                                  />
                                }
                                label=""
                                sx={{ m: 0 }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  color: credential.active
                                    ? "success.main"
                                    : "text.secondary",
                                  fontWeight: "bold",
                                  mt: 0.5,
                                }}
                              >
                                {credential.active ? "Active" : "Inactive"}
                              </Typography>
                            </Box>
                            <IconButton
                              color="warning"
                              onClick={() => editJTLCredential(credential)}
                              title="Edit Credential"
                              disabled={loading}
                              sx={{
                                backgroundColor: "warning.main",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "warning.dark",
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() => deleteJTLCredential(credential.id)}
                              title="Delete Credential"
                              disabled={loading}
                              sx={{
                                backgroundColor: "error.main",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "error.dark",
                                  transform: "scale(1.1)",
                                },
                                transition: "all 0.2s ease",
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Grid>
                        </Grid>
                      </ListItem>
                    ))}
                    {JTLCredentials.length === 0 && !loading && (
                      <ListItem>
                        <ListItemText
                          primary="No credentials configured"
                          secondary="Click 'Add Credentials' to get started"
                          sx={{ textAlign: "center" }}
                        />
                      </ListItem>
                    )}
                  </>
                )}
              </List>
            )}
          </CardContent>
        </Card>
      </Grid>
      {/* JTL Credential Dialog */}
      <Dialog
        open={JTLcredentialDialog}
        onClose={() => {
          setJTLCredentialDialog(false);
          setEditingJTLCredential(null);
          setJTLForm({
            name: "",
            client_id: "",
            client_secret: "",
            callback_url: "http://localhost:8003/inbound/jtl/oauth/callback",
            scopes: availableJTLScopes
              .filter((s) => s.defaultEnabled)
              .map((s) => s.value),
          });
        }}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            ...cp.dialogPaper,
          },
        }}
      >
        <DialogTitle
          sx={{
            ...cp.dialogHeader,
            color: "white",
            textAlign: "center",
            py: 3,
            fontSize: "1.5rem",
            fontWeight: "bold",
            borderRadius: "12px 12px 0 0",
          }}
        >
          {editingJTLCredential
            ? "✏️ Edit JTL API Credentials"
            : "➕ Add JTL API Credentials"}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={4} sx={{ mt: 1 }}>
            {/* Credential Name - full width */}
            <Grid item xs={12}>
              <TextField
                label="Credential Name"
                value={JTLForm.name}
                onChange={(e) =>
                  setJTLForm({ ...JTLForm, name: e.target.value })
                }
                fullWidth
                required
                InputProps={{ sx: { fontSize: "1.2rem", height: 56 } }}
              />
            </Grid>
            {/* Client ID and Client Secret - same row, double width */}
            <Grid item xs={12} container spacing={3}>
              <Grid item xs={6}>
                <TextField
                  label="Client ID"
                  value={JTLForm.client_id}
                  onChange={(e) =>
                    setJTLForm({ ...JTLForm, client_id: e.target.value })
                  }
                  fullWidth
                  required
                  InputProps={{ sx: { fontSize: "1.2rem", height: 56 } }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Client Secret"
                  value={JTLForm.client_secret}
                  onChange={(e) =>
                    setJTLForm({ ...JTLForm, client_secret: e.target.value })
                  }
                  fullWidth
                  required
                  InputProps={{ sx: { fontSize: "1.2rem", height: 56 } }}
                />
              </Grid>
            </Grid>
            {/* Callback URL - full width */}
            <Grid item xs={12}>
              <TextField
                label="Callback Url"
                value={JTLForm.callback_url}
                onChange={(e) =>
                  setJTLForm({ ...JTLForm, callback_url: e.target.value })
                }
                fullWidth
                required
                InputProps={{ sx: { fontSize: "1.2rem", height: 56 } }}
              />
            </Grid>

            {/* Scopes */}
            <Grid item xs={12} sx={{ mt: 14, pl: 0, ml: -37 }}>
              <Typography variant="h6" gutterBottom>
                API Scopes Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary" paragraph>
                Select the JTL API scopes needed for your use case.
              </Typography>
              {/* Group scopes by usage type */}
              {["Merchant", "Fullfiller"].map((usageType) => {
                const scopesForType = availableJTLScopes.filter(
                  (scope) => scope.usage === usageType,
                );
                if (scopesForType.length === 0) return null;
                return (
                  <Paper
                    key={usageType}
                    sx={{ p: 2, mb: 2, backgroundColor: "grey.50" }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      {usageType} Scopes
                    </Typography>
                    {scopesForType.map((scope) => (
                      <FormControlLabel
                        key={scope.value}
                        control={
                          <Switch
                            checked={JTLForm.scopes.includes(scope.value)}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setJTLForm((prev) => ({
                                ...prev,
                                scopes: isChecked
                                  ? [...prev.scopes, scope.value]
                                  : prev.scopes.filter(
                                      (s) => s !== scope.value,
                                    ),
                              }));
                            }}
                            color="primary"
                          />
                        }
                        label={
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {scope.label}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {scope.description}
                            </Typography>
                          </Box>
                        }
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          width: "100%",
                          mb: 1,
                          "& .MuiFormControlLabel-label": {
                            ml: 1,
                          },
                        }}
                      />
                    ))}
                  </Paper>
                );
              })}
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ p: 4, pt: 0, justifyContent: "space-between" }}>
          <Button
            onClick={() => {
              setJTLCredentialDialog(false);
              setEditingJTLCredential(null);
              setJTLForm({
                name: "",
                client_id: "",
                client_secret: "",
                callback_url:
                  "http://localhost:8003/inbound/jtl/oauth/callback",
                scopes: availableJTLScopes
                  .filter((s) => s.defaultEnabled)
                  .map((s) => s.value),
              });
            }}
            sx={{
              borderRadius: "50px",
              px: { xs: 4, sm: 8 },
              py: 2,
              fontWeight: "bold",
              color: "#5b4e77",
              backgroundColor: "#f4f5f7",
              boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#e8eaed",
                transform: "translateY(-3px)",
                boxShadow: "0 6px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleJTLCredentialSubmit}
            variant="contained"
            disabled={
              loading ||
              !JTLForm.name ||
              !JTLForm.client_id ||
              !JTLForm.client_secret ||
              !JTLForm.scopes.length
            }
            sx={{
              borderRadius: "50px",
              px: { xs: 5, sm: 10 },
              py: 2,
              fontWeight: "bold",
              ...cp.ctaButton,
              color: "white",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 6px 10px 4px rgba(33, 203, 243, .4)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : editingJTLCredential ? (
              "Update Credentials"
            ) : (
              "Start OAuth Flow"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* JTL Auth URL Dialog */}
      <Dialog
        open={JTLAuthUrlDialog}
        onClose={handleJTLAuthDialogClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            ...cp.dialogPaper,
          },
        }}
      >
        <DialogTitle
          sx={{
            ...cp.dialogHeader,
            color: "white",
            textAlign: "center",
            py: 3,
            fontSize: "1.5rem",
            fontWeight: "bold",
            borderRadius: "12px 12px 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleIcon />
          Authentication Required
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Next Step:</strong> You need to authenticate with JTL to
              complete the setup.
            </Typography>
          </Alert>

          <Typography variant="body1" gutterBottom>
            Click the button below to authenticate your JTL credentials:
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              my: 3,
            }}
          >
            <Button
              variant="contained"
              size="large"
              color="primary"
              startIcon={<SecurityIcon />}
              onClick={() => window.open(JTLAuthUrl, "_blank")}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: "1.1rem",
                fontWeight: "bold",
                borderRadius: "50px",
                textTransform: "none",
                ...cp.ctaButton,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 6px 10px 4px rgba(33, 203, 243, .4)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            >
              Authenticate JTL Credentials
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>What happens next:</strong>
            </Typography>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              <li>A new tab will open with JTL's authentication page</li>
              <li>Grant the requested permissions</li>
              <li>You'll be redirected back automatically</li>
              <li>Close this dialog once authentication is complete</li>
            </ul>
          </Alert>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            <strong>Auth URL:</strong> {JTLAuthUrl}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 0, justifyContent: "space-between" }}>
          <Button
            onClick={handleJTLAuthDialogClose}
            sx={{
              borderRadius: "50px",
              px: { xs: 4, sm: 8 },
              py: 2,
              fontWeight: "bold",
              color: "#5b4e77",
              backgroundColor: "#f4f5f7",
              boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#e8eaed",
                transform: "translateY(-3px)",
                boxShadow: "0 6px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => window.open(JTLAuthUrl, "_blank")}
            startIcon={<SecurityIcon />}
            sx={{
              borderRadius: "50px",
              px: { xs: 5, sm: 10 },
              py: 2,
              fontWeight: "bold",
              ...cp.ctaButton,
              color: "white",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 6px 10px 4px rgba(33, 203, 243, .4)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
            }}
          >
            Authenticate Now
          </Button>
        </DialogActions>
      </Dialog>

      {/* Auth URL Dialog */}
      <Dialog
        open={authUrlDialog}
        onClose={handleAuthDialogClose}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0,0,0,0.15)",
            ...cp.dialogPaper,
          },
        }}
      >
        <DialogTitle
          sx={{
            ...cp.dialogHeader,
            color: "white",
            textAlign: "center",
            py: 3,
            fontSize: "1.5rem",
            fontWeight: "bold",
            borderRadius: "12px 12px 0 0",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 1,
          }}
        >
          <CheckCircleIcon />
          Authentication Required
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Next Step:</strong> You need to authenticate with Google
              to complete the setup.
            </Typography>
          </Alert>

          <Typography variant="body1" gutterBottom>
            Click the button below to authenticate your Google credentials:
          </Typography>

          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              my: 3,
            }}
          >
            <Button
              variant="contained"
              size="large"
              color="primary"
              startIcon={<SecurityIcon />}
              onClick={() => window.open(authUrl, "_blank")}
              sx={{
                py: 1.5,
                px: 4,
                fontSize: "1.1rem",
                fontWeight: "bold",
                borderRadius: "50px",
                textTransform: "none",
                ...cp.ctaButton,
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "scale(1.05)",
                  boxShadow: "0 6px 10px 4px rgba(33, 203, 243, .4)",
                },
                "&:active": {
                  transform: "scale(0.95)",
                },
              }}
            >
              Authenticate Google Credentials
            </Button>
          </Box>

          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>What happens next:</strong>
            </Typography>
            <ul style={{ margin: "8px 0", paddingLeft: "20px" }}>
              <li>A new tab will open with Google's authentication page</li>
              <li>Sign in with your Google account</li>
              <li>Grant the requested permissions</li>
              <li>You'll be redirected back automatically</li>
              <li>Close this dialog once authentication is complete</li>
            </ul>
          </Alert>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 2, display: "block" }}
          >
            <strong>Auth URL:</strong> {authUrl}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 4, pt: 0, justifyContent: "space-between" }}>
          <Button
            onClick={handleAuthDialogClose}
            sx={{
              borderRadius: "50px",
              px: { xs: 4, sm: 8 },
              py: 2,
              fontWeight: "bold",
              color: "#5b4e77",
              backgroundColor: "#f4f5f7",
              boxShadow: "0 4px 6px rgba(0,0,0,0.05)",
              transition: "all 0.3s ease",
              "&:hover": {
                backgroundColor: "#e8eaed",
                transform: "translateY(-3px)",
                boxShadow: "0 6px 12px rgba(0,0,0,0.1)",
              },
            }}
          >
            Close
          </Button>
          <Button
            onClick={() => window.open(authUrl, "_blank")}
            startIcon={<SecurityIcon />}
            sx={{
              borderRadius: "50px",
              px: { xs: 5, sm: 10 },
              py: 2,
              fontWeight: "bold",
              ...cp.ctaButton,
              color: "white",
              transition: "transform 0.2s, box-shadow 0.2s",
              "&:hover": {
                transform: "scale(1.05)",
                boxShadow: "0 6px 10px 4px rgba(33, 203, 243, .4)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
            }}
          >
            Authenticate Now
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
};

export default JTLCredentials;
